// =============================================================================
// ADRESSE AUTOCOMPLETE - Composant d'autocomplétion d'adresse via API gouv.fr
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../../components/ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

// API Base - API Adresse du gouvernement français
const API_BASE = 'https://api-adresse.data.gouv.fr/search';

export const AdresseAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = "Saisissez une adresse...",
  className,
  disabled = false,
  minChars = 3,
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (query.length < minChars) {
      setSuggestions([]);
      return;
    }

    // Debounce
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}?q=${encodeURIComponent(query)}&limit=6`);
        const data = await res.json();
        
        setSuggestions(data.features?.map(f => ({
          label: f.properties.label,
          name: f.properties.name,
          housenumber: f.properties.housenumber,
          street: f.properties.street,
          postcode: f.properties.postcode,
          city: f.properties.city,
          context: f.properties.context, // ex: "75, Paris, Île-de-France"
          coordinates: f.geometry.coordinates, // [lon, lat]
        })) || []);
        
        setShowDropdown(true);
      } catch (err) {
        console.error('Erreur API Adresse:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, minChars]);

  const handleSelect = (suggestion) => {
    setQuery(suggestion.label);
    setShowDropdown(false);
    setSuggestions([]);
    
    // Appeler le callback avec les données structurées
    onSelect?.({
      adresse_complete: suggestion.label,
      numero: suggestion.housenumber || '',
      rue: suggestion.street || suggestion.name,
      code_postal: suggestion.postcode,
      ville: suggestion.city,
      region: extractRegion(suggestion.context),
      coordinates: suggestion.coordinates,
    });
    
    onChange?.(suggestion.label);
  };

  // Extraire la région du contexte (ex: "75, Paris, Île-de-France")
  const extractRegion = (context) => {
    if (!context) return '';
    const parts = context.split(',').map(s => s.trim());
    return parts[parts.length - 1] || '';
  };

  // Navigation clavier
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  const clearInput = () => {
    setQuery('');
    setSuggestions([]);
    onChange?.('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange?.(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("pl-10 pr-10 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]", className)}
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)] animate-spin" />
        ) : query && (
          <button
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)]"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown des suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg shadow-xl max-h-64 overflow-auto"
        >
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(suggestion)}
              className={cn(
                "w-full text-left px-4 py-2.5 hover:bg-[var(--oh-bg-elevated)] transition-colors",
                "flex flex-col border-b border-[var(--oh-border)] last:border-0",
                idx === selectedIndex && "bg-[var(--oh-bg-elevated)]"
              )}
            >
              <span className="text-[var(--oh-text)] text-sm font-medium">
                {suggestion.label}
              </span>
              <span className="text-[var(--oh-text-secondary)] text-xs mt-0.5">
                {suggestion.context}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdresseAutocomplete;
